import { create } from 'zustand';
import { dealerApi } from '@/lib/api/dealer';
import type {
  Company,
  DealerUserPublic,
  DealerAuthResult,
  LoginDealerInput,
  RegisterDealerInput,
  DealerSocialAuthInput,
} from '@/types';

interface DealerAuthState {
  user: DealerUserPublic | null;
  company: Company | null;
  isLoading: boolean;
  error: string | null;

  setSession: (result: DealerAuthResult) => void;
  clearSession: () => void;

  login: (input: LoginDealerInput) => Promise<void>;
  register: (input: RegisterDealerInput) => Promise<{ companyId: string; userId: string }>;
  socialRegister: (input: DealerSocialAuthInput) => Promise<void>;
  logout: () => Promise<void>;
  loadMe: () => Promise<void>;
}

export const useDealerAuthStore = create<DealerAuthState>((set) => ({
  user: null,
  company: null,
  isLoading: false,
  error: null,

  setSession(result) {
    localStorage.setItem('dealerToken', result.token);
    localStorage.setItem('dealerRefreshToken', result.refreshToken);
    set({ user: result.user, company: result.company, error: null });
  },

  clearSession() {
    localStorage.removeItem('dealerToken');
    localStorage.removeItem('dealerRefreshToken');
    set({ user: null, company: null, error: null });
  },

  async login(input) {
    set({ isLoading: true, error: null });
    try {
      const { data } = await dealerApi.login(input);
      localStorage.setItem('dealerToken', data.token);
      localStorage.setItem('dealerRefreshToken', data.refreshToken);
      set({ user: data.user, company: data.company, isLoading: false, error: null });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      set({
        isLoading: false,
        error: err.response?.data?.error ?? 'LOGIN_FAILED',
      });
      throw e;
    }
  },

  async register(input) {
    set({ isLoading: true, error: null });
    try {
      const { data } = await dealerApi.register(input);
      set({ isLoading: false });
      return { companyId: data.companyId, userId: data.userId };
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      set({
        isLoading: false,
        error: err.response?.data?.error ?? 'REGISTRATION_FAILED',
      });
      throw e;
    }
  },

  async socialRegister(input) {
    set({ isLoading: true, error: null });
    try {
      const { data } = await dealerApi.socialRegister(input);
      localStorage.setItem('dealerToken', data.token);
      localStorage.setItem('dealerRefreshToken', data.refreshToken);
      set({ user: data.user, company: data.company, isLoading: false, error: null });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string; message?: string } } };
      set({
        isLoading: false,
        error: err.response?.data?.message ?? err.response?.data?.error ?? 'SOCIAL_AUTH_FAILED',
      });
      throw e;
    }
  },

  async logout() {
    const refreshToken = localStorage.getItem('dealerRefreshToken') ?? undefined;
    try {
      await dealerApi.logout(refreshToken);
    } catch {
      // ignore
    }
    localStorage.removeItem('dealerToken');
    localStorage.removeItem('dealerRefreshToken');
    set({ user: null, company: null, error: null });
  },

  async loadMe() {
    const token = localStorage.getItem('dealerToken');
    if (!token) {
      set({ user: null, company: null });
      return;
    }
    try {
      const { data } = await dealerApi.me();
      set({ user: data.user, company: data.company });
    } catch {
      localStorage.removeItem('dealerToken');
      localStorage.removeItem('dealerRefreshToken');
      set({ user: null, company: null });
    }
  },
}));
